/**
 * Catalog of "preformatted" labels offered by the Print page.
 *
 * Each entry maps to an agent template (`document`). Fixed-text labels have an
 * empty `fields` array — the user just picks and prints. Fielded labels declare
 * the inputs the user fills before printing.
 *
 * IMPORTANT: a `document` here is the contract with the backend — the
 * sm4rt-api `/api/print` endpoint must map it to a 90mm agent template, and the
 * `field.name` keys must match that template's placeholders. See
 * docs/print-labels-backend-handoff.md.
 */

/** One user-fillable input on a preformatted label. */
export interface LabelField {
  /** Template placeholder key — sent as `data[name]`. */
  name: string;
  /** UI label shown next to the input. */
  label: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
}

/** A selectable preformatted label template. */
export interface LabelCatalogEntry {
  /** Agent template name — sent as the request `document`. */
  document: string;
  /** Display name in the catalog picker. */
  title: string;
  /** Short helper text under the picker. */
  description?: string;
  /** Inputs to fill. Empty = fixed-text label (nothing to fill). */
  fields: LabelField[];
}

/**
 * The built-in single-field label documents (PIN / password). Kept here so the
 * `document` names live in one place alongside the preformatted catalog.
 */
export const PIN_LABEL_DOCUMENT = 'pin-label';
export const PASSWORD_LABEL_DOCUMENT = 'password-label';

/**
 * Customer label — logo + customer name/phone/email + a QR that deep-links to
 * the customer page (`/customer/:dataverseCustomerId`). Not a catalog entry
 * because its data comes from a picked customer record, not free-text fields.
 */
export const CUSTOMER_LABEL_DOCUMENT = 'customer-label';

/**
 * Preformatted label catalog. Seeded with a couple of fixed-text labels and a
 * couple of fielded ones; extend as templates are added on the agent.
 */
export const LABEL_CATALOG: LabelCatalogEntry[] = [
  {
    document: 'tested-working',
    title: 'Tested & Working',
    description: 'Fixed label — device passed testing.',
    fields: [],
  },
  {
    document: 'awaiting-parts',
    title: 'Awaiting Parts',
    description: 'Fixed label — job is waiting on parts.',
    fields: [],
  },
  {
    document: 'data-backed-up',
    title: 'Data Backed Up',
    description: 'Fixed label — customer data has been backed up.',
    fields: [],
  },
  {
    document: 'return-address',
    title: 'TechSolvers Return Address',
    description: 'Fixed label — TechSolvers return address.',
    fields: [],
  },
  {
    document: 'device-tag',
    title: 'Device tag',
    description: 'Identify a device on the bench or shelf.',
    fields: [
      { name: 'customer', label: 'Customer', placeholder: 'J. Smith', required: true, maxLength: 60 },
      { name: 'device', label: 'Device', placeholder: 'iPhone 13', required: true, maxLength: 60 },
      { name: 'reference', label: 'Reference', placeholder: 'Ticket / job ref', maxLength: 40 },
    ],
  },
  {
    document: 'service-label',
    title: 'Service label',
    description: 'Record a service performed with a date.',
    fields: [
      { name: 'service', label: 'Service', placeholder: 'Annual service', required: true, maxLength: 60 },
      { name: 'technician', label: 'Technician', placeholder: 'Name / initials', maxLength: 40 },
      { name: 'date', label: 'Date', placeholder: 'DD/MM/YYYY', maxLength: 10 },
    ],
  },
];
